import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { isAlphaInviteOnlyUi } from './alpha-invite-ui.js';
import { useAuth } from './AuthProvider.js';
import { RegisterLegalConsent } from './RegisterLegalConsent.js';
import { RegisterAsModelLink } from './RegisterAsModelLink.js';
import { RegisterNameFields } from './RegisterNameFields.js';
import { resolveAppUrl } from '../urls.js';
const fieldStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
    marginBottom: '1rem',
};
const ADJECTIVES = [
    'velvet',
    'shadow',
    'neon',
    'crimson',
    'midnight',
    'electric',
    'satin',
    'onyx',
];
const NOUNS = ['fox', 'panther', 'kitten', 'raven', 'viper', 'wolf', 'sphinx', 'cobra'];
function suggestUsername() {
    const adjective = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)] ?? 'velvet';
    const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)] ?? 'fox';
    const suffix = Math.floor(Math.random() * 90) + 10;
    return `${adjective}_${noun}${suffix}`;
}
export function RegisterPage({ afterRegisterPath = '/onboarding/experience', collectLegalName = false, } = {}) {
    const { register } = useAuth();
    const navigate = useNavigate();
    const inviteOnly = isAlphaInviteOnlyUi(import.meta.env);
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [firstName, setFirstName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [lastName, setLastName] = useState('');
    const [password, setPassword] = useState('');
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const handleSuggestUsername = useCallback(() => {
        setUsername(suggestUsername());
    }, []);
    if (inviteOnly) {
        // Studio has no /accept-invite — send models to the consumer accept flow.
        if (collectLegalName) {
            const href = `${resolveAppUrl('consumer', import.meta.env).replace(/\/$/, '')}/accept-invite`;
            if (typeof window !== 'undefined') {
                window.location.replace(href);
            }
            return _jsx("p", { className: "fm-muted", children: t('common.auth.loading') });
        }
        return _jsx(Navigate, { to: "/accept-invite", replace: true });
    }
    async function handleSubmit(event) {
        event.preventDefault();
        setSubmitting(true);
        setError(null);
        if (!acceptedTerms) {
            setError(t('common.auth.termsRequired'));
            setSubmitting(false);
            return;
        }
        try {
            await register({
                email,
                username,
                password,
                ...(collectLegalName
                    ? {
                        firstName,
                        middleName: middleName.trim() ? middleName : undefined,
                        lastName,
                    }
                    : {}),
            });
            void navigate(afterRegisterPath, { replace: true });
        }
        catch {
            setError(t('common.auth.registerError'));
        }
        finally {
            setSubmitting(false);
        }
    }
    return (_jsxs("section", { className: "register-page", children: [_jsx("div", { className: "register-page__promo", "aria-hidden": "true", children: _jsxs("div", { className: "register-page__promo-overlay", children: [_jsx("h2", { className: "register-page__promo-title", children: t('common.auth.registerPromoTitle') }), _jsxs("ul", { className: "register-page__promo-list", children: [_jsx("li", { children: t('common.auth.registerPromoFeature1') }), _jsx("li", { children: t('common.auth.registerPromoFeature2') }), _jsx("li", { children: t('common.auth.registerPromoFeature3') }), _jsx("li", { children: t('common.auth.registerPromoFeature4') })] })] }) }), _jsxs("div", { className: "register-page__form-panel", children: [_jsx("h1", { className: "register-page__title", children: t('common.auth.registerTitle') }), _jsx("p", { className: "register-page__subtitle", children: t('common.auth.registerSubtitle') }), _jsxs("form", { onSubmit: (event) => void handleSubmit(event), noValidate: true, children: [collectLegalName ? (_jsx(RegisterNameFields, { firstName: firstName, middleName: middleName, lastName: lastName, onFirstNameChange: setFirstName, onMiddleNameChange: setMiddleName, onLastNameChange: setLastName })) : null, _jsxs("label", { style: fieldStyle, children: [_jsx("span", { children: t('common.auth.username') }), _jsxs("div", { className: "register-page__username-row", children: [_jsx("input", { type: "text", autoComplete: "username", required: true, minLength: 3, maxLength: 30, pattern: "[a-zA-Z0-9_]+", value: username, onChange: (event) => {
                                                    setUsername(event.target.value);
                                                }, className: "fm-input register-page__username-input" }), _jsx("button", { type: "button", className: "register-page__suggest", onClick: handleSuggestUsername, "aria-label": t('common.auth.suggestUsername'), title: t('common.auth.suggestUsername'), children: "\u2726" })] }), _jsx("span", { className: "register-page__hint", children: t('common.auth.usernameHint') })] }), _jsxs("label", { style: fieldStyle, children: [_jsx("span", { children: t('common.auth.password') }), _jsx("input", { type: "password", autoComplete: "new-password", required: true, minLength: 8, value: password, onChange: (event) => {
                                            setPassword(event.target.value);
                                        }, className: "fm-input" })] }), _jsxs("label", { style: fieldStyle, children: [_jsx("span", { children: t('common.auth.emailOptional') }), _jsx("input", { type: "email", autoComplete: "email", required: true, value: email, onChange: (event) => {
                                            setEmail(event.target.value);
                                        }, className: "fm-input" }), _jsx("span", { className: "register-page__hint", children: t('common.auth.emailOptionalHint') })] }), _jsx(RegisterLegalConsent, { id: "register-page-age-consent", checked: acceptedTerms, disabled: submitting, onCheckedChange: setAcceptedTerms }), error ? (_jsx("p", { role: "alert", className: "fm-error", children: error })) : null, _jsx(Button, { label: t('common.auth.submitRegister'), type: "submit", disabled: submitting || !acceptedTerms })] }), _jsxs("p", { className: "register-page__footer", children: [t('common.auth.hasAccount'), ' ', _jsx(Link, { to: "/login", children: t('common.nav.signIn') })] }), _jsx(RegisterAsModelLink, {})] })] }));
}
//# sourceMappingURL=RegisterPage.js.map